import { Box, Typography, CircularProgress, Link } from "@material-ui/core";
import { useMemo, useState } from "react";
import HideImageIcon from "@mui/icons-material/HideImage";

function ImageWithFallback({ url, style }) {
  const [hasError, setHasError] = useState(false);

  const handleImageError = () => {
    setHasError(true);
  };

  if (hasError) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        style={{
          minHeight: "156px",
          color: "#ccc",
          height: "100%",
          borderRadius: "10px",
          border: "1px solid #ccc",
          ...style,
        }}
      >
        <HideImageIcon style={{ fontSize: 60, marginBottom: "1rem" }} />
        <Typography variant="caption" color="textPrimary">
          Imagem não disponível
        </Typography>
      </Box>
    );
  }

  return (
    <img
      src={url}
      alt="Preview da Imagem"
      onError={handleImageError}
      style={{
        maxWidth: "100%",
        maxHeight: "60vh",
        objectFit: "contain",
        ...style,
      }}
    />
  );
}

export default function DocumentPreview({ url, style }) {
  const isBase64 = (url) => {
    if (!url || typeof url !== "string") return false;
    return url.startsWith("data:");
  };

  const getMimeTypeFromBase64 = (base64String) => {
    try {
      const match = base64String.match(/^data:([^;]+);base64,/);
      return match ? match[1] : null;
    } catch (e) {
      return null;
    }
  };

  const getFileExtension = (url) => {
    try {
      return new URL(url).pathname.split(".").pop().toLowerCase();
    } catch (e) {
      console.error("URL inválida para obter extensão:", url);
      return "";
    }
  };

  const extension = useMemo(() => getFileExtension(url), [url]);
  const mimeType = useMemo(() => {
    if (isBase64(url)) {
      return getMimeTypeFromBase64(url);
    }
    return null;
  }, [url]);

  if (isBase64(url) && mimeType && mimeType.startsWith("image/")) {
    return (
      <img
        src={url}
        alt="Preview da Imagem"
        style={{
          maxWidth: "100%",
          maxHeight: "40vh",
          objectFit: "contain",
        }}
      />
    );
  }

  if (!extension && !isBase64(url)) {
    return (
      <Box textAlign="center" p={5}>
        <CircularProgress />
        <Typography style={{ marginTop: "1rem" }}>
          Carregando documento...
        </Typography>
      </Box>
    );
  }

  switch (true) {
    case ["jpg", "jpeg", "png", "gif", "webp"].includes(extension):
      return <ImageWithFallback url={url} style={style} />;

    case ["mp4", "webm", "ogg"].includes(extension):
      return (
        <video
          src={url}
          controls
          style={{ width: "100%", height: "auto", maxHeight: "60vh", ...style }}
        >
          Seu navegador não suporta a tag de vídeo.
        </video>
      );

    case extension === "pdf":
      return (
        <iframe
          src={url}
          style={{ width: "100%", height: "60vh", border: "none", ...style }}
          title="Preview do PDF"
        />
      );

    case ["doc", "docx", "ppt", "pptx"].includes(extension): {
      const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(
        url
      )}&embedded=true`;
      return (
        <iframe
          src={googleViewerUrl}
          style={{ width: "100%", height: "60vh", border: "none", ...style }}
          title="Preview de Documento"
        />
      );
    }

    case ["txt", "csv", "rtf", "xml"].includes(extension):
      return (
        <iframe
          src={url}
          style={{
            width: "100%",
            height: "60vh",
            border: "1px solid #ccc",
            background: "#f5f5f5",
            ...style,
          }}
          title="Preview de Arquivo"
        />
      );

    default:
      return (
        <Box textAlign="center" p={3} border="1px dashed grey">
          <Typography>
            A pré-visualização não está disponível para este tipo de arquivo.
          </Typography>
          <Link href={url} target="_blank" rel="noopener noreferrer">
            Clique aqui para baixar o documento
          </Link>
        </Box>
      );
  }
}
